/*
 * Installer address handling (checkout autofill + map popup) - 04SEP26, updated 04SEP26.
 *
 * Both features below key off the same fact: the "Fitting & Balancing" / installer picker on
 * the product page (Rangiora / Sockburn) is the BSS Product Options app, not theme code, so
 * neither Shopify checkout nor the rest of the theme knows the installers' addresses on their
 * own. INSTALLER_ADDRESSES is the one place that maps an installer name to its address for
 * both features - update it there if an address changes or a partner is added/removed.
 *
 * FEATURE 1 - checkout address autofill (original, 04SEP26).
 * BSS Product Options only writes cart line-item properties ("Fitting & Balancing",
 * "<Region> (Installation Fee Per Tyre)", "Preferred Installation Date") - Shopify's checkout
 * has no concept of those properties, so a customer who picked an installer still lands on
 * checkout with a blank delivery address and has to type the installer's address in
 * themselves. Shopify checkout accepts a shipping address as URL query parameters
 * (checkout[shipping_address][address1] etc. - see
 * https://shopify.dev/docs/apps/build/checkout/create-cart-permalinks). This intercepts the
 * two theme "Checkout" buttons (cart page + header cart drawer, both
 * button[name="checkout"]), reads the current cart, and - if every line item that has an
 * installer property agrees on the same installer - sends the browser straight to
 * /checkout?checkout[shipping_address]... prefilled with that installer's address instead of
 * the plain /checkout the button would otherwise submit to.
 *
 * "Fitting Not Required" (own address) and an empty/ambiguous cart explicitly CLEAR
 * address1/city/zip via the same query params, sent as empty strings, rather than just
 * omitting them. Shopify checkout remembers whatever address was last saved against a cart
 * across visits - it is not reset just because the cart's contents changed - so a customer who
 * picks an installer (saving that address to checkout), goes back, and switches to "Fitting
 * Not Required" would otherwise still see the installer's stale address at checkout (confirmed
 * by reproducing it directly). Plain /checkout with no params at all does NOT clear an
 * already-saved address. province is a <select> in checkout with no genuinely blank option, so
 * an empty value can't clear it there - a minor cosmetic leftover, not a wrong-delivery risk,
 * since address1/city/zip (what actually determines where the parcel goes) do clear correctly.
 *
 * KNOWN LIMITATION: this only covers the Add to cart -> Cart -> Checkout button path. The
 * accelerated checkout buttons (Shop Pay / Apple Pay / Google Pay) rendered directly on the
 * product page via {{ form | payment_button }} go straight to a Shopify-hosted flow this
 * script cannot intercept, so those buyers still have to enter their own address. There is no
 * theme-side workaround for that.
 *
 * The option NAME must match what BSS Product Options sends, case-sensitive. Note BSS writes
 * the fee onto the value too (cart property value is literally "Sockburn (+ $34.99)",
 * confirmed by inspecting /cart.js after a real Add to cart), so matching is prefix-based
 * (installerNameIn below) rather than an exact match against the raw property value.
 *
 * FEATURE 2 - installer address map popup (added 04SEP26, link shortened to "See map" and
 * laid out name-left/link-right on 04SEP26, then restricted to the dropdown list only on
 * 04SEP26 - it also appeared on the post-selection "swatch" line under the field label,
 * which read as redundant/cluttered once an installer was already picked).
 * Customers had no way to see where "Rangiora" or "Sockburn" actually are before checking
 * out. BSS Product Options renders the bare installer name in the dropdown list, which this
 * watches (list items get freshly re-rendered by the app on every selection, hence the
 * MutationObserver rather than a one-off pass) and appends a "See map" link to, laid out on
 * the same row as the name via flex/space-between, that opens a small modal with an embedded
 * Google Map for that installer's address. Uses the key-less
 * https://www.google.com/maps?q=...&output=embed form, so no Google Maps API key is needed.
 */
(function () {
  'use strict';

  var INSTALLER_ADDRESSES = {
    'Rangiora': {
      address1: '343e Flaxton Road',
      city: 'Rangiora',
      province: 'Canterbury',
      zip: '7400',
      country: 'New Zealand'
    },
    'Sockburn': {
      address1: '1/22 Lowther Street',
      city: 'Christchurch',
      province: 'Canterbury',
      zip: '8042',
      country: 'New Zealand'
    }
  };

  // BSS Product Options writes the installer name with its per-tyre fee appended, e.g.
  // "Sockburn (+ $34.99)" - not the bare name. Match on the name leading the value rather
  // than an exact match, so a fee-formatting change in the app doesn't silently break this.
  function installerNameIn(rawValue) {
    var value = (rawValue || '').trim();
    for (var name in INSTALLER_ADDRESSES) {
      if (!Object.prototype.hasOwnProperty.call(INSTALLER_ADDRESSES, name)) continue;
      if (value === name || value.indexOf(name + ' ') === 0 || value.indexOf(name + '(') === 0) {
        return name;
      }
    }
    return null;
  }

  // Looks at every property of every cart line and returns the installer name if all lines
  // that named one agree on the same installer. Returns null for no match or a disagreement
  // (mixed installers in one cart can't be represented by a single checkout address, so we
  // fall back to letting the customer fill in their own address rather than guess).
  function getSelectedInstaller(cart) {
    var found = null;
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var properties = items[i].properties || {};
      for (var key in properties) {
        if (!Object.prototype.hasOwnProperty.call(properties, key)) continue;
        var name = installerNameIn(properties[key]);
        if (name) {
          if (found === null) {
            found = name;
          } else if (found !== name) {
            return null; // different installers in the same cart - ambiguous
          }
        }
      }
    }
    return found;
  }

  // Shopify checkout remembers whatever shipping address was last saved against a given cart,
  // across visits - it isn't reset just because the cart's contents changed. So a customer who
  // picks an installer (saving that address to checkout), then goes back and switches to
  // "Fitting Not Required", would otherwise still see the installer's address at checkout:
  // confirmed by reproducing it directly (BUG, 04SEP26 follow-up). Sending empty values for
  // address1/city/zip explicitly clears them (verified - a plain '/checkout' with no params at
  // all does NOT clear an already-saved address, it just leaves it as-is). province is a
  // <select> in checkout, which has no genuinely blank option to select via an empty value, so
  // it's left holding its last value - a minor cosmetic leftover, not a wrong-delivery risk,
  // since address1/city/zip (the fields that actually determine where the parcel goes) do clear.
  function checkoutUrlFor(installerName) {
    var address = installerName ? INSTALLER_ADDRESSES[installerName] : null;
    var params = new URLSearchParams();
    params.set('checkout[shipping_address][address1]', address ? address.address1 : '');
    params.set('checkout[shipping_address][city]', address ? address.city : '');
    params.set('checkout[shipping_address][zip]', address ? address.zip : '');
    if (address) {
      params.set('checkout[shipping_address][province]', address.province);
      params.set('checkout[shipping_address][country]', address.country);
    }
    return '/checkout?' + params.toString();
  }

  function goToCheckout() {
    fetch('/cart.js', {credentials: 'same-origin'})
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        window.location.href = checkoutUrlFor(getSelectedInstaller(cart));
      })
      .catch(function () {
        // Cart lookup failed for some reason - fail open to the normal, unprefilled checkout
        // rather than block the purchase.
        window.location.href = '/checkout';
      });
  }

  // Capture-phase click listener on the document catches both theme checkout buttons (cart
  // page: #checkout, header cart drawer: unnamed button[name="checkout"]) without needing a
  // separate handler wired up per section, and keeps working if either section is re-rendered
  // by the theme's AJAX cart updates. Cancelling the click cancels the button's default
  // "submit this form" action, so the normal POST to /cart never happens.
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('button[name="checkout"]');
    if (!trigger || trigger.disabled) return;
    event.preventDefault();
    goToCheckout();
  }, true);

  // ---- Installer address map popup ---------------------------------------------------

  function mapEmbedUrlFor(installerName) {
    var address = INSTALLER_ADDRESSES[installerName];
    if (!address) return null;
    var query = address.address1 + ', ' + address.city + ' ' + address.zip + ', ' + address.country;
    return 'https://www.google.com/maps?q=' + encodeURIComponent(query) + '&output=embed';
  }

  var mapModal = null;

  function buildMapModal() {
    var overlay = document.createElement('div');
    overlay.id = 'installer-map-modal';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.style.cssText = 'display:none;position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,0.6);align-items:center;justify-content:center;padding:24px;';

    var box = document.createElement('div');
    box.style.cssText = 'background:#fff;border-radius:8px;max-width:560px;width:100%;overflow:hidden;box-shadow:0 10px 40px rgba(0,0,0,0.3);';

    var header = document.createElement('div');
    header.style.cssText = 'display:flex;align-items:center;justify-content:space-between;padding:14px 18px;border-bottom:1px solid #e6e9ef;';

    var title = document.createElement('span');
    title.id = 'installer-map-modal-title';
    title.style.cssText = 'font-weight:700;font-size:16px;color:#22292f;';

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Close map');
    closeBtn.textContent = '×';
    closeBtn.style.cssText = 'background:none;border:0;font-size:26px;line-height:1;cursor:pointer;color:#687080;padding:0 4px;';
    closeBtn.addEventListener('click', closeMapModal);

    header.appendChild(title);
    header.appendChild(closeBtn);

    var iframe = document.createElement('iframe');
    iframe.id = 'installer-map-modal-frame';
    iframe.title = 'Installer location map';
    iframe.width = '100%';
    iframe.height = '360';
    iframe.style.cssText = 'border:0;display:block;';
    iframe.loading = 'lazy';
    iframe.referrerPolicy = 'no-referrer-when-downgrade';

    box.appendChild(header);
    box.appendChild(iframe);
    overlay.appendChild(box);

    // Click the dark backdrop (not the box itself) to close, same convention as the theme's
    // own modals.
    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) closeMapModal();
    });

    document.body.appendChild(overlay);
    return overlay;
  }

  function closeMapModalOnEscape(event) {
    if (event.key === 'Escape') closeMapModal();
  }

  function openMapModal(installerName) {
    var url = mapEmbedUrlFor(installerName);
    if (!url) return;
    if (!mapModal) mapModal = buildMapModal();
    mapModal.querySelector('#installer-map-modal-title').textContent = installerName + ' fitting partner';
    mapModal.querySelector('#installer-map-modal-frame').src = url;
    mapModal.style.display = 'flex';
    document.addEventListener('keydown', closeMapModalOnEscape);
  }

  function closeMapModal() {
    if (!mapModal) return;
    mapModal.style.display = 'none';
    // Drop the iframe src while hidden so the map isn't still loading/tracking in the background.
    mapModal.querySelector('#installer-map-modal-frame').src = 'about:blank';
    document.removeEventListener('keydown', closeMapModalOnEscape);
  }

  function buildMapLink(name) {
    var link = document.createElement('span');
    link.textContent = 'See map';
    link.style.cssText = 'color:#1f6fd6;text-decoration:underline;cursor:pointer;font-weight:600;white-space:nowrap;flex-shrink:0;';
    link.setAttribute('role', 'button');
    link.setAttribute('tabindex', '0');
    // stopPropagation so this doesn't also bubble up to BSS Product Options' own click
    // handler on the dropdown wrapper, which would select the option just from opening its map.
    link.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      openMapModal(name);
    });
    link.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        openMapModal(name);
      }
    });
    return link;
  }

  // Puts the installer name and the "See map" link on one row, name on the left and the
  // link pushed to the right edge.
  // flex-direction is forced with setProperty(...,'important') because BSS Product Options'
  // own stylesheet sets .po_description to flex-direction:column, which otherwise wins over
  // a plain inline style and stacks the name and link instead of placing them side by side
  // (confirmed via getComputedStyle - found this the hard way after the first version shipped
  // with the two stacked instead of in a row).
  function layoutNameAndLinkAsRow(container) {
    container.style.display = 'flex';
    container.style.setProperty('flex-direction', 'row', 'important');
    container.style.alignItems = 'center';
    container.style.justifyContent = 'space-between';
    container.style.gap = '12px';
  }

  // BSS Product Options re-renders the dropdown list and the "selected" swatch on every
  // change, so a one-off pass at page load would miss anything shown after the customer
  // interacts with the field - hence the flag-and-observe pattern instead of running once.
  //
  // .po_option_value (the dropdown LIST item) is not just a label - clicking an option makes
  // BSS Product Options read this element's own textContent back to build both the "selected"
  // display AND (per the fee-suffix format found earlier) the cart line-item property value.
  // Appending the map link INSIDE it would get swept up into both of those, corrupting the
  // real cart data - so it goes in as a next sibling instead (into .po_description, the
  // element's parent), which keeps el.textContent clean while the flex layout on that parent
  // still lines the two up on one row.
  function enhanceDropdownOption(el) {
    if (el.dataset.mapLinkAdded) return;
    var name = (el.textContent || '').trim();
    if (!Object.prototype.hasOwnProperty.call(INSTALLER_ADDRESSES, name)) return;
    el.dataset.mapLinkAdded = 'true';
    el.parentNode.insertBefore(buildMapLink(name), el.nextSibling);
    layoutNameAndLinkAsRow(el.parentNode);
  }

  // Deliberately NOT applied to .po-selected-swatch .swatch_value (the post-selection line
  // under the field label, e.g. "Sockburn" shown once picked) - a merchant request, 04SEP26:
  // once an installer is already selected, a second "See map" there read as redundant next
  // to the one still visible in the (still open) dropdown list below it.
  function scanForInstallerLabels(root) {
    root.querySelectorAll('.po_option_value').forEach(enhanceDropdownOption);
  }

  var installerLabelObserver = new MutationObserver(function () {
    scanForInstallerLabels(document);
  });
  installerLabelObserver.observe(document.body, {childList: true, subtree: true});
  scanForInstallerLabels(document);
})();
